/**
 * GpsTrailModal — dispatcher "View GPS trail" modal (Phase 8 / Task 8.5).
 *
 * Renders a job's GPS breadcrumb trail on the shared Mapbox map: a route line +
 * time-graded dots (blue → green), event pins for non-POSITION points, and the
 * geofence arrival events with their §15 verification result. KML + CSV export.
 * Empty state when no trail was recorded.
 */

import * as React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { useSharedMap } from '../map/MapProvider.js';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { useApiOpts } from '@lodex-pro/web-shared';
import { useGpsTrail, type GpsTrailPoint, type GpsTrail } from './use-completed-jobs.js';
import { MAP_COLOURS } from '../../lib/map-colours';
import { trailToCsv, trailToKml, downloadFile } from './gps-export.js';

type ApiOpts = ReturnType<typeof useApiOpts>;

const SRC = 'gps-trail';

export interface GpsTrailModalProps {
  readonly apiOpts: ApiOpts;
  readonly jobId: string | null;
  readonly haulId: string | null;
  readonly open: boolean;
  readonly onClose: () => void;
}

function buildFeatureCollection(points: readonly GpsTrailPoint[]): GeoJSON.FeatureCollection {
  const valid = points.filter((p) => p.lat != null && p.lng != null);
  const line: GeoJSON.Feature = {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: valid.map((p) => [p.lng as number, p.lat as number]),
    },
    properties: { kind: 'line' },
  };
  const dots: GeoJSON.Feature[] = valid.map((p, i) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [p.lng as number, p.lat as number] },
    properties: {
      kind: p.eventType === 'POSITION' ? 'position' : 'event',
      t: valid.length <= 1 ? 1 : i / (valid.length - 1),
      eventType: p.eventType,
    },
  }));
  return { type: 'FeatureCollection', features: [line, ...dots] };
}

function GpsTrailMap({ trail }: { trail: GpsTrail }): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const { acquire, release } = useSharedMap();
  const fc = useMemo(() => buildFeatureCollection(trail.points), [trail.points]);

  useEffect(() => {
    if (!containerRef.current) return;
    const map = acquire(containerRef.current);
    if (!map) return;
    mapRef.current = map;
    map.setStyle('mapbox://styles/mapbox/satellite-streets-v12');

    const draw = (): void => {
      if (!map.getSource(SRC)) {
        map.addSource(SRC, { type: 'geojson', data: fc });
        map.addLayer({
          id: `${SRC}-line`,
          type: 'line',
          source: SRC,
          filter: ['==', '$type', 'LineString'],
          paint: { 'line-color': MAP_COLOURS.accent, 'line-width': 2, 'line-opacity': 0.7 },
        });
        map.addLayer({
          id: `${SRC}-dots`,
          type: 'circle',
          source: SRC,
          filter: ['all', ['==', '$type', 'Point'], ['==', 'kind', 'position']],
          paint: {
            'circle-radius': 3,
            // time-graded blue (start) → green (end)
            'circle-color': [
              'interpolate',
              ['linear'],
              ['get', 't'],
              0,
              MAP_COLOURS.accent,
              1,
              MAP_COLOURS.success,
            ],
          },
        });
        map.addLayer({
          id: `${SRC}-events`,
          type: 'circle',
          source: SRC,
          filter: ['all', ['==', '$type', 'Point'], ['==', 'kind', 'event']],
          paint: {
            'circle-radius': 6,
            'circle-color': MAP_COLOURS.warning,
            'circle-stroke-color': MAP_COLOURS.paper,
            'circle-stroke-width': 2,
          },
        });
      } else {
        const src = map.getSource<mapboxgl.GeoJSONSource>(SRC);
        src?.setData(fc);
      }
      // Fit bounds to the trail.
      const coords =
        (fc.features[0]?.geometry as GeoJSON.LineString | undefined)?.coordinates ?? [];
      if (coords.length > 0) {
        const b = new mapboxgl.LngLatBounds();
        for (const c of coords) b.extend(c as [number, number]);
        map.fitBounds(b, { padding: 48, maxZoom: 16, duration: 0 });
      }
    };

    if (map.isStyleLoaded()) draw();
    map.on('style.load', draw);
    window.setTimeout(() => map.resize(), 0);

    return () => {
      map.off('style.load', draw);
      for (const id of [`${SRC}-line`, `${SRC}-dots`, `${SRC}-events`]) {
        if (map.getLayer(id)) map.removeLayer(id);
      }
      if (map.getSource(SRC)) map.removeSource(SRC);
      mapRef.current = null;
      release();
    };
  }, [acquire, release, fc]);

  return <div ref={containerRef} style={{ flex: 1, minHeight: '420px', width: '100%' }} />;
}

export function GpsTrailModal({
  apiOpts,
  jobId,
  haulId,
  open,
  onClose,
}: GpsTrailModalProps): React.JSX.Element {
  const { data, isLoading } = useGpsTrail(apiOpts, open ? jobId : null);
  const points = data?.points ?? [];
  const hasTrail = points.some((p) => p.lat != null && p.lng != null);

  const exportName = `gps-trail-${haulId ?? jobId ?? 'job'}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent style={{ maxWidth: '820px', width: '90vw' }}>
        <DialogHeader>
          <DialogTitle>GPS trail{haulId != null ? ` — ${haulId}` : ''}</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div
            style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-secondary)' }}
          >
            Loading trail…
          </div>
        ) : !hasTrail ? (
          <div
            style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-tertiary)' }}
          >
            No GPS trail recorded for this job
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: '420px' }}>
            <GpsTrailMap trail={data ?? { points: [], geofenceEvents: [] }} />
          </div>
        )}

        <DialogFooter>
          {hasTrail && (
            <>
              <Button
                variant="outline"
                onClick={() =>
                  downloadFile(`${exportName}.csv`, trailToCsv(points), 'text/csv;charset=utf-8')
                }
              >
                Export CSV
              </Button>
              <Button
                variant="outline"
                onClick={() =>
                  downloadFile(
                    `${exportName}.kml`,
                    trailToKml(points, exportName),
                    'application/vnd.google-earth.kml+xml',
                  )
                }
              >
                Export KML
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
